/* eslint-disable no-param-reassign */

/* eslint-disable class-methods-use-this */
import { Layout, Size } from './types';
import { diffArray } from './utils';

export type Range = {
    start: number;
    end: number;
};

export type Range2D = {
    x: Range;
    y: Range;
};

export type Indexes = {
    now: number[][];
    nowIn: number[][];
    nowOut: number[][];
};

export type AllIndexes = {
    visible?: Indexes;
    engaged?: Indexes;
};

export type OnIndexesChanged = (indexes: AllIndexes) => void;

enum MoveDirection {
    up,
    down,
    left,
    right,
    unchanged,
}

/*
    Class that is responsible for calculating visible / engaged (to be rendered) item's indexes.
    Receives offset updates (x, y), does calculations, fires events:
    - onVisibleIndexesChanged: visible indexes per row
    - onEngagedIndexesChanged: engaged (to be rendered) indexes per row

    Both events are working with 2D arrays, first level indicating the row, second level indicating
    the indexes per row (visible/engaged). Example for a 5 row grid:
    [
        []          // No items to be rendered on first row
        [2, 3, 4]   // Items with indexes 2, 3 and 4 to be rendered on second row
        [1, 2]      // Items with indexes 1 and 2 to be rendered on third row
        [3, 4]      // Itmes with indexes 3 and 4 to be rendered on fourth row
        []          // No items to be rendered on last row
    ]
*/

export default class VisibilityTracker {
    private _currentOffsetX: number;

    private _currentOffsetY: number;

    private _renderAheadOffsetHorizontal: number;

    private _renderAheadOffsetVertical: number;

    private _visibleWindow: Range2D;

    private _engagedWindow: Range2D;

    private _windowSize: Size;

    private _visibleIndexes: number[][];

    private _engagedIndexes: number[][];

    private _layouts: Layout[][] = [];

    private _rowHeight: number;

    private _onIndexesChanged: OnIndexesChanged;

    constructor(
        rowHeight: number,
        renderAheadOffsetHorizontal: number,
        renderAheadOffsetVertical: number,
        onIndexesChanged: OnIndexesChanged,
    ) {
        this._rowHeight = rowHeight;
        this._currentOffsetX = 0;
        this._currentOffsetY = 0;
        this._renderAheadOffsetHorizontal = renderAheadOffsetHorizontal;
        this._renderAheadOffsetVertical = renderAheadOffsetVertical;
        this._visibleWindow = {
            x: { start: 0, end: 0 },
            y: { start: 0, end: 0 },
        };
        this._engagedWindow = {
            x: { start: 0, end: 0 },
            y: { start: 0, end: 0 },
        };

        this._windowSize = { width: 0, height: 0 };

        this._visibleIndexes = [];
        this._engagedIndexes = [];

        this._onIndexesChanged = onIndexesChanged;
    }

    public setLayouts = (layouts: Layout[][]) => {
        this._layouts = layouts;
        this.updateOffset(this._currentOffsetX, this._currentOffsetY, true);
    };

    public setWindowSize = (dimension: Size) => {
        this._windowSize = dimension;
        this.updateOffset(this._currentOffsetX, this._currentOffsetY, true);
    };

    public updateOffset = (
        offsetX: number,
        offsetY: number,
        force?: boolean,
    ) => {
        if (this._windowSize.width === 0 || this._windowSize.height === 0) {
            return;
        }

        if (
            force ||
            this._currentOffsetX !== offsetX ||
            this._currentOffsetY !== offsetY
        ) {
            let dir = MoveDirection.unchanged;
            if (this._currentOffsetX > offsetX) {
                dir = MoveDirection.left;
            } else if (this._currentOffsetX < offsetX) {
                dir = MoveDirection.right;
            } else if (this._currentOffsetY > offsetY) {
                dir = MoveDirection.up;
            } else if (this._currentOffsetY < offsetY) {
                dir = MoveDirection.down;
            }
            this._updateTrackingWindows(offsetX, offsetY);
            this._updateIndexes(dir);
            this._currentOffsetX = offsetX;
            this._currentOffsetY = offsetY;
        }
    };

    public reset = () => {
        this._visibleIndexes = [];
        this._engagedIndexes = [];
    };

    private _updateTrackingWindows = (offsetX: number, offsetY: number) => {
        this._visibleWindow = {
            x: { start: offsetX, end: offsetX + this._windowSize.width },
            y: { start: offsetY, end: offsetY + this._windowSize.height },
        };
        this._engagedWindow = {
            x: {
                start: Math.max(0, offsetX - this._renderAheadOffsetHorizontal),
                end:
                    offsetX +
                    this._windowSize.width +
                    this._renderAheadOffsetHorizontal,
            },
            y: {
                start: Math.max(0, offsetY - this._renderAheadOffsetVertical),
                end:
                    offsetY +
                    this._windowSize.height +
                    this._renderAheadOffsetVertical,
            },
        };
    };

    private _updateIndexes = (dir: MoveDirection) => {
        const engStart = Math.floor(
            this._engagedWindow.y.start / this._rowHeight,
        );
        const engEnd = Math.floor(this._engagedWindow.y.end / this._rowHeight);
        const visStart = Math.floor(
            this._visibleWindow.y.start / this._rowHeight,
        );
        const visEnd = Math.floor(this._visibleWindow.y.end / this._rowHeight);
        const rowsCount = this._layouts.length;
        const newEngagedIndexes: number[][] = Array(rowsCount)
            .fill(0)
            .map(() => []);
        const newVisibleIndexes: number[][] = Array(rowsCount)
            .fill(0)
            .map(() => []);

        for (let i = 0; i < rowsCount; i += 1) {
            if (i >= engStart && i <= engEnd) {
                const previousEngagedRowIndexes = this._engagedIndexes[i] ?? [];
                switch (dir) {
                    case MoveDirection.up:
                    case MoveDirection.down: {
                        if (previousEngagedRowIndexes.length) {
                            newEngagedIndexes[i] = previousEngagedRowIndexes;
                            if (i >= visStart && i <= visEnd) {
                                newVisibleIndexes[i] =
                                    previousEngagedRowIndexes;
                            }
                        } else {
                            this._checkAndUpdateRow(
                                this._layouts[i],
                                previousEngagedRowIndexes,
                                newEngagedIndexes[i],
                                newVisibleIndexes[i],
                                dir,
                            );
                        }
                        break;
                    }

                    default:
                        this._checkAndUpdateRow(
                            this._layouts[i],
                            previousEngagedRowIndexes,
                            newEngagedIndexes[i],
                            newVisibleIndexes[i],
                            dir,
                        );
                        break;
                }
            }
        }

        this._fireEvent(newVisibleIndexes, newEngagedIndexes);

        this._visibleIndexes = newVisibleIndexes;
        this._engagedIndexes = newEngagedIndexes;
    };

    private _checkAndUpdateRow = (
        rowLayouts: Layout[],
        previousEngagedRowIndexes: number[],
        newEngagedRowIndexes: number[],
        newVisibleRowIndexes: number[],
        dir: MoveDirection,
    ) => {
        let startIndex = 0;
        let reverse = false;
        const previousEngagedRowIndexesLength =
            previousEngagedRowIndexes.length;
        if (dir === MoveDirection.right) {
            startIndex = previousEngagedRowIndexesLength
                ? previousEngagedRowIndexes[0]
                : 0;
        } else if (dir === MoveDirection.left) {
            startIndex = previousEngagedRowIndexesLength
                ? previousEngagedRowIndexes[previousEngagedRowIndexesLength - 1]
                : rowLayouts.length - 1;
            reverse = true;
        }
        this._updateRow(
            startIndex,
            rowLayouts,
            newEngagedRowIndexes,
            newVisibleRowIndexes,
            reverse,
        );
    };

    private _updateRow = (
        startIndex: number,
        rowLayouts: Layout[],
        newEngagedRowIndexes: number[],
        newVisibleRowIndexes: number[],
        reverse: boolean,
    ) => {
        let itemRange: Range2D = {
            x: { start: 0, end: 0 },
            y: { start: 0, end: 0 },
        };
        let oneFound = false;

        if (reverse) {
            for (let index = startIndex; index >= 0; index -= 1) {
                itemRange = this._getItemRange(rowLayouts[index]);
                if (this._intersects(this._visibleWindow, itemRange)) {
                    newEngagedRowIndexes.unshift(index);
                    newVisibleRowIndexes.unshift(index);
                    oneFound = true;
                } else if (this._intersects(this._engagedWindow, itemRange)) {
                    newEngagedRowIndexes.unshift(index);
                    oneFound = true;
                } else if (oneFound) {
                    break;
                }
            }
        } else {
            const numberOfItems = rowLayouts.length;
            for (let index = startIndex; index < numberOfItems; index += 1) {
                itemRange = this._getItemRange(rowLayouts[index]);
                if (this._intersects(this._visibleWindow, itemRange)) {
                    newEngagedRowIndexes.push(index);
                    newVisibleRowIndexes.push(index);
                    oneFound = true;
                } else if (this._intersects(this._engagedWindow, itemRange)) {
                    newEngagedRowIndexes.push(index);
                    oneFound = true;
                } else if (oneFound) {
                    break;
                }
            }
        }
    };

    private _getItemRange = (itemLayout: Layout): Range2D => {
        const { x, y, width, height } = itemLayout;
        return {
            x: {
                start: x,
                end: x + width,
            },
            y: {
                start: y,
                end: y + height,
            },
        };
    };

    private _intersectsOnAxis = (r1: Range, r2: Range) => {
        return !(r1.start > r2.end || r1.end < r2.start);
    };

    private _intersects = (r1: Range2D, r2: Range2D) => {
        return (
            this._intersectsOnAxis(r1.x, r2.x) &&
            this._intersectsOnAxis(r1.y, r2.y)
        );
    };

    private _fireEvent = (
        newVisibleIndexes: number[][],
        newEngagedIndexes: number[][],
    ) => {
        const indexes: AllIndexes = {};
        const [needsReportingV] = this._checkChanges(
            newVisibleIndexes,
            this._visibleIndexes,
        );
        indexes.visible = {
            now: newVisibleIndexes,
            nowIn: [],
            nowOut: [],
        };

        const [needsReportingE, nowInE, nowOutE] = this._checkChanges(
            newEngagedIndexes,
            this._engagedIndexes,
        );
        indexes.engaged = {
            now: newEngagedIndexes,
            nowIn: nowInE,
            nowOut: nowOutE,
        };

        if (needsReportingE || needsReportingV) {
            this._onIndexesChanged(indexes);
        }
    };

    private _checkChanges = (
        newArr: number[][],
        prevArr: number[][],
    ): [boolean, number[][], number[][]] => {
        const { length } = newArr;
        const newIn: number[][] = [];
        const newOut: number[][] = [];
        let needsReporting = false;
        for (let i = 0; i < length; i += 1) {
            const newRow = newArr[i];
            const prevRow = prevArr[i] ?? [];
            const newRowIn = diffArray(newRow, prevRow);
            const newRowOut = diffArray(prevRow, newRow);
            newIn.push(newRowIn);
            newOut.push(newRowOut);
            if (!needsReporting && (newRowIn.length || newRowOut.length)) {
                needsReporting = true;
            }
        }
        return [needsReporting, newIn, newOut];
    };
}
